import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  FolderTree,
  Globe,
  Mail,
  Shield,
  Sliders,
  Zap,
} from "lucide-react";
import {
  useAdminCompany,
  useAdminCompanyUsage,
  useChangeCompanyStatus,
  useChangeTier,
  useOverrideCompanyLimits,
  type OverrideLimitsRequest,
} from "../../api/admin";
import { extractErrorMessage } from "../../api/errors";
import { startActingAsCompany } from "../../auth/session";
import { StatusBadge } from "../../components/StatusBadge";
import { UsageBar } from "../../components/UsageBar";

const tiers = ["basic", "standard", "premium"];

function parseLimitField(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  if (trimmed === "-" || trimmed.toLowerCase() === "unlimited") return -1;
  const n = Number(trimmed);
  if (Number.isNaN(n) || !Number.isFinite(n)) return undefined;
  return n;
}

export function AdminCompanyDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const companyQuery = useAdminCompany(tenantId ?? "");
  const usageQuery = useAdminCompanyUsage(tenantId ?? "");
  const changeTier = useChangeTier();
  const changeStatus = useChangeCompanyStatus();
  const overrideLimits = useOverrideCompanyLimits();

  const company = companyQuery.data?.data;
  const usage = usageQuery.data?.data;

  const [devicesInput, setDevicesInput] = useState("");
  const [usersInput, setUsersInput] = useState("");
  const [groupsInput, setGroupsInput] = useState("");
  const [firmwareInput, setFirmwareInput] = useState("");
  const [tasksInput, setTasksInput] = useState("");
  const [overrideError, setOverrideError] = useState("");
  const [overrideSuccess, setOverrideSuccess] = useState(false);

  function enterCompanyWorkspace(nextPath: string): void {
    if (!tenantId || !company) {
      return;
    }

    startActingAsCompany(tenantId, company.companyName);
    queryClient.clear();
    navigate(nextPath);
  }

  async function submitOverrides(e: FormEvent) {
    e.preventDefault();
    setOverrideError("");
    setOverrideSuccess(false);

    const limits: OverrideLimitsRequest = {
      devices: parseLimitField(devicesInput),
      users: parseLimitField(usersInput),
      deviceGroups: parseLimitField(groupsInput),
      firmware: parseLimitField(firmwareInput),
      concurrentTasks: parseLimitField(tasksInput),
    };

    const payload = Object.fromEntries(
      Object.entries(limits).filter(([, value]) => value !== undefined),
    ) as OverrideLimitsRequest;

    if (Object.keys(payload).length === 0) {
      setOverrideError("Enter at least one limit to override.");
      return;
    }

    try {
      await overrideLimits.mutateAsync({ tenantId: tenantId!, limits: payload });
      setOverrideSuccess(true);
      setDevicesInput("");
      setUsersInput("");
      setGroupsInput("");
      setFirmwareInput("");
      setTasksInput("");
    } catch (err) {
      setOverrideError(extractErrorMessage(err, "Override failed"));
    }
  }

  if (companyQuery.isLoading && !company) {
    return (
      <div className="py-20 text-center text-sm text-gray-500 dark:text-riot-navy-300">
        Loading company details...
      </div>
    );
  }

  if (!company) {
    return (
      <div>
        <button
          onClick={() => navigate("/admin/companies")}
          className="mb-4 flex items-center gap-1 text-xs text-riot-gold-600 hover:underline dark:text-riot-gold-400"
        >
          <ArrowLeft size={14} /> Back to companies
        </button>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          We could not load that company. It may have been removed or the route is not returning a record yet.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate("/admin/companies")}
        className="flex items-center gap-1 text-xs text-riot-gold-600 hover:underline dark:text-riot-gold-400"
      >
        <ArrowLeft size={14} /> Back to companies
      </button>

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-riot-gold-100 text-riot-gold-600 dark:bg-riot-gold-500/10 dark:text-riot-gold-400">
                <Building2 size={22} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-riot-gold-600 dark:text-riot-gold-400">
                  Company workspace
                </p>
                <h2 className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{company.companyName}</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-riot-navy-300">
                  Use this company as the working context before adding devices, creating groups, and reviewing tenant data.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">
                  <Mail size={13} /> Contact
                </div>
                <div className="font-medium text-gray-800 dark:text-riot-navy-100">{company.contactEmail}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">
                  <Globe size={13} /> Timezone
                </div>
                <div className="font-medium text-gray-800 dark:text-riot-navy-100">{company.timezone || "Not set"}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">Tenant ID</div>
                <div className="font-mono text-xs text-gray-700 dark:text-riot-navy-100">{company.tenantId}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">Status</div>
                <StatusBadge status={company.status} />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 lg:min-w-[240px]">
            <button
              onClick={() => enterCompanyWorkspace("/groups")}
              className="flex items-center justify-center gap-2 rounded-md bg-riot-gold-400 px-4 py-2.5 text-sm font-medium text-riot-navy-950 hover:bg-riot-gold-500 active:bg-riot-gold-600"
            >
              <FolderTree size={15} /> Manage groups
            </button>
            <button
              onClick={() => enterCompanyWorkspace("/")}
              className="flex items-center justify-center gap-2 rounded-md border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"
            >
              Open company workspace <ArrowRight size={15} />
            </button>
            <p className="text-xs text-gray-500 dark:text-riot-navy-300">
              We’ll keep your platform-admin access, but tenant pages will use this company context until you exit it.
            </p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-riot-navy-700 dark:bg-riot-navy-800">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
            <Zap size={16} className="text-amber-500" /> Subscription Tier
          </h3>
          <p className="mb-3 text-sm text-gray-500 dark:text-riot-navy-300">
            Current: <span className="font-medium capitalize text-gray-900 dark:text-white">{company.subscriptionTier || "-"}</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {tiers.map((tier) => (
              <button
                key={tier}
                onClick={() => changeTier.mutate({ tenantId: tenantId!, tier })}
                disabled={changeTier.isPending || company.subscriptionTier === tier}
                className={`rounded-md px-4 py-2 text-xs font-medium capitalize ${
                  company.subscriptionTier === tier
                    ? "bg-riot-gold-100 text-riot-gold-700 dark:bg-riot-gold-500/10 dark:text-riot-gold-400"
                    : "border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"
                } disabled:opacity-50`}
              >
                {tier}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-riot-navy-700 dark:bg-riot-navy-800">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
            <Shield size={16} className="text-green-500" /> Tenant Status
          </h3>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-riot-navy-300">Current:</span>
            <StatusBadge status={company.status} />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => changeStatus.mutate({ tenantId: tenantId!, status: "active" })}
              disabled={changeStatus.isPending || company.status === "active"}
              className="rounded-md bg-green-600 px-4 py-2 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              Activate
            </button>
            <button
              onClick={() => changeStatus.mutate({ tenantId: tenantId!, status: "suspended" })}
              disabled={changeStatus.isPending || company.status === "suspended"}
              className="rounded-md bg-orange-600 px-4 py-2 text-xs font-medium text-white hover:bg-orange-700 disabled:opacity-50"
            >
              Suspend
            </button>
          </div>
        </div>

        <div className="col-span-full rounded-lg border border-gray-200 bg-white p-5 dark:border-riot-navy-700 dark:bg-riot-navy-800">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
            <Sliders size={16} className="text-riot-gold-500" /> Override Limits
          </h3>
          <p className="mb-4 text-xs text-gray-500 dark:text-riot-navy-300">
            Overrides apply on top of the current tier defaults. Leave a field blank to keep the existing limit; enter <code>-</code> for unlimited.
          </p>
          <form onSubmit={submitOverrides} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-700 dark:text-riot-navy-200">Devices</span>
              <input
                aria-label="Devices limit"
                type="text"
                inputMode="numeric"
                value={devicesInput}
                onChange={(e) => setDevicesInput(e.target.value)}
                placeholder={usage ? String(usage.devices.limit) : String(company.maxDevices)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-700 dark:text-riot-navy-200">Users</span>
              <input
                aria-label="Users limit"
                type="text"
                inputMode="numeric"
                value={usersInput}
                onChange={(e) => setUsersInput(e.target.value)}
                placeholder={usage ? String(usage.users.limit) : String(company.maxUsers)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-700 dark:text-riot-navy-200">Device Groups</span>
              <input
                aria-label="Device groups limit"
                type="text"
                inputMode="numeric"
                value={groupsInput}
                onChange={(e) => setGroupsInput(e.target.value)}
                placeholder={usage ? String(usage.deviceGroups.limit) : String(company.maxGroups)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-700 dark:text-riot-navy-200">Firmware</span>
              <input
                aria-label="Firmware limit"
                type="text"
                inputMode="numeric"
                value={firmwareInput}
                onChange={(e) => setFirmwareInput(e.target.value)}
                placeholder={usage ? String(usage.firmware.limit) : String(company.maxFirmware)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-700 dark:text-riot-navy-200">Concurrent Tasks</span>
              <input
                aria-label="Concurrent tasks limit"
                type="text"
                inputMode="numeric"
                value={tasksInput}
                onChange={(e) => setTasksInput(e.target.value)}
                placeholder={usage ? String(usage.concurrentTasks.limit) : String(company.maxConcurrentTasks)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
              />
            </label>

            <div className="col-span-full flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={overrideLimits.isPending}
                className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
              >
                {overrideLimits.isPending ? "Saving…" : "Apply overrides"}
              </button>
              {overrideError && <span className="text-xs text-red-600 dark:text-red-400">{overrideError}</span>}
              {overrideSuccess && <span className="text-xs text-green-600 dark:text-green-400">Overrides applied.</span>}
            </div>
          </form>
        </div>

        {usage && (
          <div className="col-span-full rounded-lg border border-gray-200 bg-white p-5 dark:border-riot-navy-700 dark:bg-riot-navy-800">
            <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">Resource Usage</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <UsageBar label="Devices" current={usage.devices.current} limit={usage.devices.limit} />
              <UsageBar label="Users" current={usage.users.current} limit={usage.users.limit} />
              <UsageBar label="Firmware" current={usage.firmware.current} limit={usage.firmware.limit} />
              <UsageBar label="Device Groups" current={usage.deviceGroups.current} limit={usage.deviceGroups.limit} />
              <UsageBar label="Concurrent Tasks" current={usage.concurrentTasks.current} limit={usage.concurrentTasks.limit} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
